/**
 * Hook for fetching dashboard metrics from the CRM.
 *
 * Fetches counts for contacts, companies, and deals, plus deal aggregation
 * data (total value, by-stage breakdown).
 */

import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useCrmAggregate } from '@/lib/crm/hooks/useCrmAggregate';
import { usePipelineStages } from '@/lib/crm/hooks/usePipelineStages';
import type { Stage } from '@/lib/crm/hooks/useStages';
import { logger } from '@/lib/logger';
import type { AssociationGroupBy, QuantityValue } from '@/types/crm';

interface DashboardAggregationGroupResult {
  group_by: {
    ahoy_deal_to_deal_pipeline_stage?: string | null; // entity_id string directly
  };
  aggregations: {
    count?: number;
    deal_count?: number;
    total_amount?: QuantityValue | QuantityValue[];
    weighted_amount?: QuantityValue | QuantityValue[];
  };
}

interface DashboardAggregationResponse {
  count: number;
  results: DashboardAggregationGroupResult[];
}

export interface DashboardMetrics {
  contactsCount: number;
  companiesCount: number;
  dealsCount: number;
  activeDealCount: number;
  totalPipelineValue: number;
  weightedPipelineValue: number;
  currencyCode: string;
  pipelineStages: {
    stageEntityId: string;
    stageLabel: string;
    dealCount: number;
    totalValue: number;
    /** Configured stage color (ahoy_color, hex/CSS). Absent = no color set. */
    stageColor?: string;
  }[];
}

const CONTACTS_COUNT_BODY = {
  entity_type_name: 'ahoy_contact',
  aggregations: [{ function: 'count' as const, alias: 'count' }],
};

const COMPANIES_COUNT_BODY = {
  entity_type_name: 'ahoy_company',
  aggregations: [{ function: 'count' as const, alias: 'count' }],
};

const DEAL_AGGREGATION_GROUP_BY: AssociationGroupBy = {
  association: 'ahoy_deal_to_deal_pipeline_stage',
};

/**
 * Parse total value from aggregation response.
 * Returns value for the specified currency (defaults to USD).
 * If no matching currency found, returns the first available amount.
 */
function parseTotalValue(
  amounts: QuantityValue | QuantityValue[] | undefined,
  preferredCurrency: string = 'USD'
): { value: number; currency: string } {
  if (!amounts) {
    return { value: 0, currency: preferredCurrency };
  }

  // Handle single-object shape (returned when convert_to is used)
  if (!Array.isArray(amounts)) {
    const numAmount =
      typeof amounts.amount === 'string'
        ? parseFloat(amounts.amount)
        : amounts.amount;
    return {
      value: Number.isNaN(numAmount) ? 0 : (numAmount ?? 0),
      currency: amounts.unit || preferredCurrency,
    };
  }

  if (amounts.length === 0) {
    return { value: 0, currency: preferredCurrency };
  }

  // Try to find the preferred currency first
  const preferredAmount = amounts.find(
    item => item.unit === preferredCurrency || item.unit === null
  );
  if (preferredAmount) {
    const numAmount =
      typeof preferredAmount.amount === 'string'
        ? parseFloat(preferredAmount.amount)
        : preferredAmount.amount;
    return {
      value: Number.isNaN(numAmount) ? 0 : (numAmount ?? 0),
      currency: preferredAmount.unit || preferredCurrency,
    };
  }

  // Fall back to first available currency
  const firstAmount = amounts[0];
  const numAmount =
    typeof firstAmount.amount === 'string'
      ? parseFloat(firstAmount.amount)
      : firstAmount.amount;
  return {
    value: Number.isNaN(numAmount) ? 0 : (numAmount ?? 0),
    currency: firstAmount.unit || preferredCurrency,
  };
}

/**
 * Transform aggregation results using stages lookup.
 */
function transformAggregationWithStages(
  dealAggregation: DashboardAggregationResponse,
  stagesByEntityId: Map<string, Stage>
): {
  dealsCount: number;
  activeDealCount: number;
  totalPipelineValue: number;
  weightedPipelineValue: number;
  currencyCode: string;
  pipelineStages: DashboardMetrics['pipelineStages'];
} {
  let dealsCount = 0;
  let activeDealCount = 0;
  let totalPipelineValue = 0;
  let weightedPipelineValue = 0;
  let detectedCurrency = 'USD';

  // Create a map for quick lookup by entity_id
  const stageMap = new Map<string, { dealCount: number; totalValue: number }>();
  // Track deals with no stage (null entity_id)
  let noStageDealCount = 0;
  let noStageTotalValue = 0;

  for (const result of dealAggregation.results) {
    // Backend returns ahoy_deal_to_deal_pipeline_stage as string | null (entity_id directly)
    const stageEntityId =
      result.group_by.ahoy_deal_to_deal_pipeline_stage ?? null;
    const dealCount = Math.round(Number(result.aggregations.deal_count) || 0);
    const { value: stageValue, currency } = parseTotalValue(
      result.aggregations.total_amount,
      detectedCurrency
    );
    const { value: stageWeightedValue } = parseTotalValue(
      result.aggregations.weighted_amount,
      detectedCurrency
    );

    // Use the first non-USD currency we find as the detected currency (only set once)
    if (detectedCurrency === 'USD' && currency && currency !== 'USD') {
      detectedCurrency = currency;
    }

    dealsCount += dealCount;

    // Exclude closed stages (probability 0 = Closed Lost, probability 1 = Closed Won)
    // from pipeline totals — only active pipeline deals count
    // Treat missing/unknown stages as closed to avoid leaking closed deals
    const stage = stageEntityId
      ? stagesByEntityId.get(stageEntityId)
      : undefined;
    const isClosedStage =
      stageEntityId != null
        ? stage == null || stage.probability === 0 || stage.probability === 1
        : false;
    if (!isClosedStage) {
      activeDealCount += dealCount;
      totalPipelineValue += stageValue;
      weightedPipelineValue += stageWeightedValue;
    }

    if (stageEntityId) {
      stageMap.set(stageEntityId, { dealCount, totalValue: stageValue });
    } else {
      // Deals with no stage
      noStageDealCount += dealCount;
      noStageTotalValue += stageValue;
    }
  }

  // Build ordered pipeline stages using stage order from useStages
  // Get deal pipeline stages sorted by order
  const dealPipelineStages = Array.from(stagesByEntityId.values())
    .filter(s => s.entity_type_name === 'ahoy_deal_pipeline_stage')
    .sort((a, b) => a.order - b.order);

  const pipelineStages: DashboardMetrics['pipelineStages'] = [];

  for (const stage of dealPipelineStages) {
    const stageData = stageMap.get(stage.entity_id);
    pipelineStages.push({
      stageEntityId: stage.entity_id,
      stageLabel: stage.label,
      dealCount: stageData?.dealCount ?? 0,
      totalValue: stageData?.totalValue ?? 0,
      stageColor: stage.color,
    });
  }

  // Add "No Stage" category if there are deals without a stage
  if (noStageDealCount > 0) {
    pipelineStages.push({
      stageEntityId: '',
      stageLabel: 'No Stage',
      dealCount: noStageDealCount,
      totalValue: noStageTotalValue,
    });
  }

  return {
    dealsCount,
    activeDealCount,
    totalPipelineValue,
    weightedPipelineValue,
    currencyCode: detectedCurrency,
    pipelineStages,
  };
}

/**
 * Hook for fetching all dashboard metrics.
 */
export function useDashboardMetrics(
  orgId: number | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled: callerEnabled = true } = options;
  const { currentOrg } = useOrg();
  const convertTo = currentOrg?.defaultCurrency;

  // Fetch stages to resolve entity_ids to labels
  const {
    stagesByEntityId,
    isLoading: stagesLoading,
    error: stagesError,
  } = usePipelineStages(orgId, 'ahoy_deal');

  const enabled = !!orgId && !!convertTo && callerEnabled;

  // Deal body depends on convertTo so cannot be a module constant
  const dealAggregationBody = useMemo(
    () => ({
      entity_type_name: 'ahoy_deal',
      group_by: [DEAL_AGGREGATION_GROUP_BY],
      aggregations: [
        { function: 'count' as const, alias: 'deal_count' },
        {
          function: 'sum' as const,
          property: 'ahoy_amount',
          alias: 'total_amount',
        },
        {
          function: 'sum' as const,
          property: 'ahoy_weighted_amount',
          alias: 'weighted_amount',
        },
      ],
      ...(convertTo !== undefined ? { convert_to: convertTo } : {}),
    }),
    [convertTo]
  );

  const contactsQuery = useCrmAggregate<DashboardAggregationResponse>(
    orgId,
    CONTACTS_COUNT_BODY,
    { enabled }
  );
  const companiesQuery = useCrmAggregate<DashboardAggregationResponse>(
    orgId,
    COMPANIES_COUNT_BODY,
    { enabled }
  );
  const dealQuery = useCrmAggregate<DashboardAggregationResponse>(
    orgId,
    dealAggregationBody,
    { enabled }
  );

  const isLoading =
    contactsQuery.isLoading || companiesQuery.isLoading || dealQuery.isLoading;
  const error = contactsQuery.error || companiesQuery.error || dealQuery.error;

  // Transform the data once stages are done loading (even if empty).
  // This allows showing valid contact/company counts for new orgs with no stages.
  const contactsCount =
    contactsQuery.data?.results.reduce(
      (sum: number, r: DashboardAggregationGroupResult) =>
        sum + (Number(r.aggregations.count) || 0),
      0
    ) ?? 0;
  const companiesCount =
    companiesQuery.data?.results.reduce(
      (sum: number, r: DashboardAggregationGroupResult) =>
        sum + (Number(r.aggregations.count) || 0),
      0
    ) ?? 0;

  const data: DashboardMetrics | undefined =
    contactsQuery.data &&
    companiesQuery.data &&
    dealQuery.data &&
    !stagesLoading
      ? {
          contactsCount,
          companiesCount,
          ...transformAggregationWithStages(dealQuery.data, stagesByEntityId),
        }
      : undefined;

  return {
    data,
    isLoading: isLoading || stagesLoading,
    isFetching:
      contactsQuery.isFetching ||
      companiesQuery.isFetching ||
      dealQuery.isFetching,
    isSuccess:
      contactsQuery.isSuccess &&
      companiesQuery.isSuccess &&
      dealQuery.isSuccess,
    isError:
      contactsQuery.isError || companiesQuery.isError || dealQuery.isError,
    error: error || stagesError,
  };
}

/**
 * Currency symbols for common currencies.
 */
const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  CAD: 'CA$',
  AUD: 'A$',
};

/**
 * Format a number as currency (e.g., $1.2M, $500K, $1,234).
 * Supports multiple currencies via the currencyCode parameter.
 *
 * Uses compact notation (K/M) when the value divides evenly into thousands,
 * otherwise shows the full amount so smaller values aren't lost to rounding.
 */
export function formatCurrency(
  value: number,
  currencyCode: string = 'USD'
): string {
  const symbol = currencySymbols[currencyCode] || currencyCode + ' ';

  if (typeof value !== 'number' || isNaN(value)) {
    logger.error(
      'formatCurrency received invalid value',
      new Error('Type validation failed'),
      { value, component: 'formatCurrency' }
    );
    return `${symbol}0`;
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000 && absValue % 1_000_000 === 0) {
    // Clean millions — show as $1M, $15M, etc.
    return `${sign}${symbol}${(absValue / 1_000_000).toFixed(0)}M`;
  }
  if (absValue >= 1_000 && absValue % 1_000 === 0) {
    // Clean thousands — show as $3K, $150K, etc.
    return `${sign}${symbol}${(absValue / 1_000).toFixed(0)}K`;
  }
  // Show full amount for values with sub-thousand precision ($3,010)
  return `${sign}${symbol}${absValue.toLocaleString()}`;
}

/**
 * Format a stage name for display (e.g., "ahoy_qualification" -> "Qualification").
 */
export function formatStageName(stageName: string): string {
  const nameMap: Record<string, string> = {
    ahoy_qualification: 'Qualification',
    ahoy_needs_analysis: 'Needs Analysis',
    ahoy_proposal: 'Proposal',
    ahoy_negotiation: 'Negotiation',
    ahoy_closed_won: 'Closed Won',
    ahoy_closed_lost: 'Closed Lost',
  };
  return nameMap[stageName] || stageName;
}
